import { GhostButton } from "@/components/common/GhostButton";
import AddTaskIcon from "@mui/icons-material/AddTask";
import SwitchRightIcon from "@mui/icons-material/SwitchRight";
import { formatDate, parseISO } from "date-fns";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  BulkDeleteButton,
  DateInput,
  NullableBooleanInput,
  NumberInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
  useUpdateMany,
} from "react-admin";
import { policyStatuses, recordTypes, reimbursementStatuses, stoplossClaimStatuses } from "../recordTypes";
import Button from "@/components/ui/Button";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

export const RecordOwnershipBulkActions = () => {
  const [open, setOpen] = useState(false);
  const { selectedIds, resource } = useListContext();
  const [updateMany, { isLoading }] = useUpdateMany();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const unselectAll = useUnselectAll(resource);
  const refresh = useRefresh();
  const [editField, setEditField] = useState("");
  const [editData, setEditData] = useState({});
  const [inputField, setInputField] = useState("");

  const recordType = recordTypes.find((record) => record.id === resource);
  const { editableKeys, name: resourceName } = recordType;

  // Allow users to submit a new change by pressing enter
  const handleUserKeyPress = (event: KeyboardEvent) => {
    const { key } = event;

    if (key === "Enter" && inputField && editField) {
      setEditData({ ...editData, [editField]: inputField });
      setEditField("");
      setInputField("");
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);

    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  });

  const handleSubmit = async () => {
    try {
      // Filter out empty values
      const dataToUpdate = Object.fromEntries(Object.entries(editData).filter(([_, value]) => value !== ""));

      await updateMany(resource, {
        ids: selectedIds,
        data: dataToUpdate,
      });

      notify(`Updated ${selectedIds.length} records`, { type: "success" });
      setOpen(false);
      unselectAll();
      refresh();
      setEditData({});
    } catch (error) {
      notifyError(error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditData({});
    setEditField("");
  };

  const getChanges = () => {
    return Object.keys(editData).map((key, idx) => {
      const keyIndex: number = recordType!.editableKeys.findIndex((potentialKey) => potentialKey.key === key);
      const booleanText = () => {
        if (typeof editData[key] !== "boolean") {
          return editData[key];
        } else if (editData[key]) {
          return "true";
        } else {
          return "false";
        }
      };

      return (
        <li key={idx}>
          Changing <b>{recordType?.editableKeys[keyIndex].name.toLowerCase()}</b> to{" "}
          <b>
            {recordType?.editableKeys[keyIndex].type === "date"
              ? formatDate(parseISO(editData[key]), "MM/dd/yyyy")
              : booleanText()}
          </b>
        </li>
      );
    });
  };

  const getInputType = () => {
    const keyIndex = recordType!.editableKeys.findIndex((object) => object.key === editField);
    const inputType = recordType!.editableKeys[keyIndex].type;
    const getSelectResource = () => {
      if (resource === "policies") {
        return policyStatuses;
      } else if (resource === "stoploss-claims") {
        return stoplossClaimStatuses;
      } else if (resource === "reimbursements") {
        return reimbursementStatuses;
      }
    };

    if (inputType === "string") {
      return (
        <TextInput
          fullWidth
          margin="normal"
          label="New Value"
          helperText={false}
          source={editField}
          value={inputField || ""}
          onChange={(e) => setInputField(e.target.value)}
          aria-label="Type out new value"
          placeholder={`What is the new ${editableKeys.find((key: { key: string; name: string }) => key.key === editField).name.toLowerCase()} value?`}
        />
      );
    } else if (inputType === "number") {
      return (
        <NumberInput
          fullWidth
          margin="normal"
          label="New Value"
          helperText={false}
          source={editField}
          value={inputField || ""}
          onChange={(e) => setInputField(e.target.value)}
          aria-label="Type out new value"
          placeholder={`What is the new ${editableKeys.find((key: { key: string; name: string }) => key.key === editField).name.toLowerCase()} value?`}
        />
      );
    } else if (inputType === "date") {
      return (
        <DateInput
          fullWidth
          margin="normal"
          label="New Value"
          helperText={false}
          source={editField}
          value={inputField || ""}
          onChange={(e) => setInputField(e.target.value)}
          aria-label="Type out new value"
          placeholder={`What is the new ${editableKeys.find((key: { key: string; name: string }) => key.key === editField).name.toLowerCase()} value?`}
        />
      );
    } else if (inputType === "select") {
      const selectData = getSelectResource();

      return (
        <SelectInput
          fullWidth
          margin="normal"
          label="New Value"
          helperText={false}
          source={editField}
          value={inputField || ""}
          onChange={(e) => setInputField(e.target.value)}
          aria-label="Type out new value"
          placeholder={`What is the new ${editableKeys.find((key: { key: string; name: string }) => key.key === editField).name.toLowerCase()} value?`}
          choices={selectData}
        />
      );
    } else if (inputType === "boolean") {
      return (
        <NullableBooleanInput
          fullWidth
          margin="normal"
          label="New Value"
          helperText={false}
          source={editField}
          value={inputField || ""}
          onChange={(e) => setInputField(e.target.value === "true")}
          aria-label="Type out new value"
        />
      );
    }
  };

  return (
    <>
      <GhostButton
        sx={{ color: "black" }}
        label="Update Selected Items"
        onClick={() => setOpen(true)}
        startIcon={<SwitchRightIcon />}
      />
      <BulkDeleteButton />

      <Dialog open={open} onClose={() => handleClose()} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reassign {selectedIds.length} Selected {resourceName} Records
        </DialogTitle>
        <DialogContent>
          <SimpleForm toolbar={false}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Field to Edit</InputLabel>
              <Select label="Field to Edit" value={editField} onChange={(event) => setEditField(event.target.value)}>
                {editableKeys.map((key: { key: string; name: string }, idx: number) => {
                  return (
                    <MenuItem value={key.key} key={idx}>
                      {key.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {editField && (
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {getInputType()}
                <GhostButton
                  onClick={() => {
                    setEditData({ ...editData, [editField]: inputField });
                    setEditField("");
                    setInputField("");
                  }}
                  startIcon={<AddTaskIcon />}
                  sx={{ height: 56, top: 5 }}
                  aria-label="Add Selection to Changes"
                />
              </Box>
            )}
            {Object.keys(editData).length > 0 && (
              <>
                <Divider sx={{ mt: "1rem" }} />
                <Box sx={{ p: "1rem" }}>
                  <Typography variant="h4">Current Updates:</Typography>
                  <ul>{getChanges()}</ul>
                </Box>
              </>
            )}
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={Object.keys(editData).length === 0} loading={isLoading}>
            Update Records
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
